import React, { Key, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from '@emotion/styled';
import { BucketLogData } from '@bucketplace/bucket-log';
import { NumberInputField, TextInputField, Toggle } from '@bucketplace/commerce-admin-ui';

import { DocumentNode } from '../../types';
import { FormContent as BaseFormContent } from '../FormContent';

export type UnitNode<T = { logData?: BucketLogData }> = Partial<Omit<DocumentNode<T>, 'children'>>;

export interface LogDataFormGroupProps {
  nodeKey?: Key | null;
  name?: string;
  label?: string;
  value?: BucketLogData;
  initialValue?: BucketLogData;
  disabledKeys?: string[];
}

export function LogDataFormGroup({
  nodeKey,
  name = 'logData',
  label = '로그',
  value,
  initialValue,
  disabledKeys,
}: LogDataFormGroupProps) {
  const { setValue, getValues } = useFormContext();

  const logData = useWatch({ name });
  const hasLogData = logData?.category != null;

  const handleToggleChange = (value: boolean) => {
    const newToggleValue = value
      ? {
        category: 'click',
        ...initialValue,
      }
      : undefined;

    setValue(name, newToggleValue);
  };

  useEffect(() => {
    if (hasLogData && initialValue) {
      setTimeout(() => {
        setValue(name, {
          category: 'click',
          ...initialValue,
        });
      }, 0);
    }
  }, [nodeKey]);

  useEffect(() => {
    if (hasLogData) {
      setValue(name, {
        ...getValues(name),
        ...value,
      });
    }
  }, [value]);

  return (
    <>
      <FormContent header={label}>
        <Toggle
          checked={hasLogData}
          onCheck={handleToggleChange}
        />
      </FormContent>
      { hasLogData && (
        <>
          <FormContent header="SECTION">
            <TextInputField
              name={`${name}.section`}
              width="100%"
              disabled={disabledKeys?.includes('section')}
            />
          </FormContent>
          <FormContent header="TYPE">
            <TextInputField
              name={`${name}.type`}
              width="100%"
              disabled={disabledKeys?.includes('type')}
            />
          </FormContent>
          <FormContent header="ID">
            <TextInputField
              name={`${name}.id`}
              width="100%"
              disabled={disabledKeys?.includes('id')}
            />
          </FormContent>
          <FormContent header="INDEX">
            <NumberInputField
              name={`${name}.index`}
              width="100%"
              disabled={disabledKeys?.includes('index')}
            />
          </FormContent>
          <FormContent header="URL">
            <TextInputField
              name={`${name}.url`}
              width="100%"
              disabled={disabledKeys?.includes('url')}
            />
          </FormContent>
        </>
      ) }
    </>
  );
}

/** 로그 Section 간격이 넓어서 커스텀하게 제거 */
const FormContent = styled(BaseFormContent)`
  > header {
    padding: 16px;
  }
  > div {
    padding: 8px 16px 0;
  }
`;
